import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import {
  esLineaDeRenting, formasDePago, lineaDeRenting, pasarARenting,
  resumenRenting, tienePlazos, tipoEfectivo,
} from '@/lib/renting';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, FileText, CheckCircle, XCircle, Send, Trash2, Calculator, Edit, Download, Copy, AlertCircle, Search, Mail, X, Upload, CreditCard, RefreshCw } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { ClientSearchSelect } from '@/components/ClientSearchSelect';
import { confirmAsync } from '@/lib/confirm';
import { AvisoFaltanDatos, DialogoDatosFacturacion } from '@/components/DatosFacturacion';

// Estos dos componentes viven FUERA de Quotes a proposito.
//
// Definidos dentro, React creaba una funcion nueva en cada render del padre.
// Al comparar elementos por identidad de tipo, veia un componente distinto y
// desmontaba el subarbol entero en vez de actualizarlo. Escribir en el precio
// o en la descripcion llamaba a updateItem -> setState en el padre -> tabla
// nueva -> el input perdia el foco a cada tecla.
  // Componente de búsqueda con autocompletar
const ProductSearchInput = ({ section, onSelect, products }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);

    useEffect(() => {
      if (searchTerm.length >= 2) {
        const filtered = products
          // `section` YA es el tipo de producto. Esto era un ternario
          // anidado cuyo else valia 'equipment', asi que cualquier
          // seccion nueva buscaba equipos sin quejarse de nada.
          //
          // Por tipo EFECTIVO: los productos del 24-sep con tipo 'renting'
          // cuentan como equipos. Si no, no saldrian en ninguna seccion, y
          // sin dar ningun error -- simplemente no estarian.
          .filter(p => tipoEfectivo(p) === section)
          .filter(p => 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.ref && p.ref.toLowerCase().includes(searchTerm.toLowerCase()))
          )
          .slice(0, 10); // Máximo 10 resultados
        setFilteredProducts(filtered);
        setShowResults(true);
      } else {
        setFilteredProducts([]);
        setShowResults(false);
      }
    }, [searchTerm, products, section]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (searchRef.current && !searchRef.current.contains(event.target)) {
          setShowResults(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (productId) => {
      onSelect(productId);
      setSearchTerm('');
      setShowResults(false);
    };

    return (
      <div className="relative w-64" ref={searchRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
            placeholder="Buscar producto..."
            className="pl-10"
          />
        </div>
        
        {showResults && filteredProducts.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {filteredProducts.map(product => (
              <div
                key={product.id}
                onClick={() => handleSelect(product.id)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm">
                      {product.ref && <span className="text-gray-500 mr-2">{product.ref}</span>}
                      {product.name}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-green-600">
                    {product.price.toFixed(2)}€
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && searchTerm.length >= 2 && filteredProducts.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-500 text-center">No se encontraron productos</p>
          </div>
        )}
      </div>
    );
  };

  // Componente para renderizar tabla de items
/** El tipo de una LINEA, traduciendo el `renting` historico del 24-sep. */
const tipoDeLinea = (item) => tipoEfectivo({ type: item?.product_type });

/** El producto del catalogo de esta linea, si sigue existiendo. */
const productoDe = (item, products) =>
  (products || []).find((p) => p.id === item?.product_id);

const ItemTable = ({ items, section, sectionName, products, onAddProduct, updateItem, removeItem }) => {
  // La columna PAGO solo aparece si en esta seccion hay algo alquilable.
  // En la mayoria de presupuestos no habra nada y la tabla queda como
  // estaba: una columna que casi nunca aplica solo estorba.
  const hayAlquilables = items.some(
    (i) => esLineaDeRenting(i) || tienePlazos(productoDe(i, products)));

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-[#212B35]">{sectionName}</h4>
        <ProductSearchInput
          section={section}
          products={products}
          onSelect={(productId) => onAddProduct(productId, section)}
        />
      </div>

      {items.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#9C0F17] text-white">
              <tr>
                <th className="p-2 text-left">REF.</th>
                <th className="p-2 text-left">NOMBRE</th>
                {hayAlquilables && <th className="p-2 text-center">PAGO</th>}
                <th className="p-2 text-right">PRECIO</th>
                <th className="p-2 text-center">UDS.</th>
                <th className="p-2 text-center">DTO.%</th>
                {section === 'license' && (
                  <>
                    <th className="p-2 text-center">FRECUENCIA</th>
                    <th className="p-2 text-center">CADA</th>
                  </>
                )}
                <th className="p-2 text-right">SUBTOTAL</th>
                <th className="p-2 text-center">-</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2 font-mono text-xs">{item.product_ref || 'N/A'}</td>
                  <td className="p-2">
                    <div>{item.product_name}</div>
                    {item.allow_custom_line ? (
                      <Textarea
                        value={item.product_description || ''}
                        onChange={(e) => updateItem(index, 'product_description', e.target.value, section)}
                        placeholder="Describe el desarrollo a medida (saldrá en el PDF)"
                        rows={2}
                        className="mt-1 text-xs min-w-[16rem]"
                      />
                    ) : item.product_description ? (
                      <div className="text-xs text-gray-500">{item.product_description}</div>
                    ) : null}
                  </td>
                  {hayAlquilables && (
                    <td className="p-2 text-center">
                      {(() => {
                        const opciones = formasDePago(productoDe(item, products));
                        // Con una sola forma no hay nada que elegir; y si el
                        // producto ya no esta en el catalogo, se dice en vez
                        // de dejar un hueco.
                        if (opciones.length < 2) {
                          return (
                            <span className="text-xs text-gray-500">
                              {esLineaDeRenting(item)
                                ? `Renting ${item.renting_meses}m`
                                : 'Compra'}
                            </span>
                          );
                        }
                        return (
                          // Un <select> normal: dentro de una celda de tabla
                          // el portal del Select se cierra al hacer scroll.
                          <select
                            value={item.renting_meses ?? ''}
                            onChange={(e) => updateItem(index, 'renting_meses', e.target.value, section)}
                            className="border rounded px-2 py-1 text-xs"
                            data-testid={`forma-pago-${index}`}
                          >
                            {opciones.map((o) => (
                              <option key={o.meses ?? 'compra'} value={o.meses ?? ''}>
                                {o.etiqueta}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>
                  )}
                  <td className="p-2 text-right">
                    {esLineaDeRenting(item) ? (
                      // La cuota la pone la financiera: no se teclea aqui.
                      <span className="whitespace-nowrap">
                        {item.unit_price.toFixed(2)}€<span className="text-xs text-gray-500">/mes</span>
                      </span>
                    ) : item.allow_custom_line ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value, section)}
                        className="w-24 text-right"
                      />
                    ) : (
                      <>{item.unit_price.toFixed(2)}€</>
                    )}
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value, section)}
                      className="w-16 text-center"
                    />
                  </td>
                  <td className="p-2">
                    {esLineaDeRenting(item) ? (
                      // Lo pactado con la financiera es la cuota. Rebajarla
                      // diria un precio que el cliente no va a pagar.
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={item.discount}
                        onChange={(e) => updateItem(index, 'discount', e.target.value, section)}
                        className="w-16 text-center"
                      />
                    )}
                  </td>
                  {section === 'license' && (
                    <>
                      <td className="p-2">
                        <Select
                          value={item.recurrence_frequency || "mensual"}
                          onValueChange={(value) => {
                            updateItem(index, 'recurrence_frequency', value, section);
                            // Reset interval to 1 when changing frequency
                            updateItem(index, 'recurrence_interval', 1, section);
                          }}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mensual">📅 Mensual</SelectItem>
                            <SelectItem value="semanal">📆 Semanal</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="1"
                          max={item.recurrence_frequency === 'semanal' ? 3 : 12}
                          value={item.recurrence_interval || 1}
                          onChange={(e) => updateItem(index, 'recurrence_interval', parseInt(e.target.value) || 1, section)}
                          className="w-16 text-center"
                          title={item.recurrence_frequency === 'semanal' ? 'Semanas (1-3)' : 'Meses (1-12)'}
                        />
                      </td>
                    </>
                  )}
                  <td className="p-2 text-right font-semibold">
                    {esLineaDeRenting(item) ? (
                      // No es "0 EUR": es que no lo cobramos nosotros. Un
                      // "0,00 EUR" aqui parece un error de precio.
                      <span className="text-xs font-normal text-amber-800"
                            title="Lo factura la entidad que financia el renting">
                        financiado
                      </span>
                    ) : (
                      <>{item.subtotal.toFixed(2)}€</>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(index, section)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function Quotes({ user }) {
  const [searchParams] = useSearchParams();
  // Presupuesto cuyo cliente tiene datos pendientes. Abre el MISMO formulario
  // que usa el CRM: un solo sitio donde se guardan, imposible desincronizar.
  const [quoteDatos, setQuoteDatos] = useState(null);
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null); // Para modo edición
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [taxRate, setTaxRate] = useState(21);
  const [taxType, setTaxType] = useState('IVA');
  const [paymentMethod, setPaymentMethod] = useState('Transferencia');
  const [quoteNotes, setQuoteNotes] = useState('');

  // Items por sección
  const [licenseItems, setLicenseItems] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  // Ya no hay estado de renting. Una linea alquilada vive en la seccion
  // de su producto -- un TPV sigue siendo un equipo -- y lo que la
  // distingue es `renting_meses` (Angel, 25-sep-2026).

  // Filtros de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, invoiced
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [copiedQuoteId, setCopiedQuoteId] = useState(null);

  // Plantillas
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  
  // Billing alert
  const [billingAlertOpen, setBillingAlertOpen] = useState(false);
  const [billingAlertNote, setBillingAlertNote] = useState('');
  const [pendingQuoteId, setPendingQuoteId] = useState(null);
  
  // Company config for transfer data
  const [companyConfig, setCompanyConfig] = useState(null);

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Estado del cobro por link, indexado por presupuesto. Se trae de una sola
  // vez: una peticion por tarjeta serian decenas para dibujar la pantalla.
  const [paymentLinks, setPaymentLinks] = useState({});
  const [regenerandoLink, setRegenerandoLink] = useState(null);

  useEffect(() => {
    fetchQuotes();
    fetchProducts();
    fetchCustomers();
    fetchTemplates();
    fetchCompanyConfig();
    fetchPaymentLinks();
    
    // Si hay parámetro de cliente en la URL, aplicar filtro y abrir modal
    const customerParam = searchParams.get('customer');
    const actionTypeParam = searchParams.get('actionType');
    
    if (customerParam) {
      setFilterCustomer(customerParam);
      setSelectedCustomer(customerParam);
      
      // Si viene de una acción "Crear Presupuesto", abrir modal automáticamente
      if (actionTypeParam) {
        setDialogOpen(true);
      }
    }
  }, [searchParams]);

  const fetchQuotes = async () => {
    try {
      const response = await axiosInstance.get('/quotes');
      setQuotes(response.data);
    } catch (error) {
      toast.error('Error al cargar presupuestos');
    }
  };

  const fetchPaymentLinks = async () => {
    try {
      const { data } = await axiosInstance.get('/payment-links');
      setPaymentLinks(data || {});
    } catch (error) {
      // Sin pasarela configurada esto no devuelve nada util, y la pantalla
      // tiene que seguir funcionando exactamente igual que antes de que los
      // links existieran. No se avisa al usuario de algo que no ha pedido.
      setPaymentLinks({});
    }
  };

  const copiarLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    } catch {
      toast.error('No se pudo copiar el link');
    }
  };

  const regenerarLink = async (quoteId) => {
    setRegenerandoLink(quoteId);
    try {
      await axiosInstance.post(`/quotes/${quoteId}/payment-link`);
      toast.success('Nuevo link de pago generado');
      fetchPaymentLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo generar el link');
    } finally {
      setRegenerandoLink(null);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axiosInstance.get('/customers');
      // El endpoint ahora devuelve paginación: {items: [], total: int}
      setCustomers(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error loading customers');
      setCustomers([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/quote-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading customers');
    }
  };

  const fetchCompanyConfig = async () => {
    try {
      const response = await axiosInstance.get('/company-config');
      setCompanyConfig(response.data);
    } catch (error) {
      console.error('Error loading company config:', error);
    }
  };

  // Las lineas guardadas antes de que existiera la linea a medida no llevan el
  // flag. Se completa desde el catalogo para que un presupuesto antiguo tambien
  // se pueda editar. Se usa ?? y no ||: un false explicito es una decision
  // guardada y se respeta; solo se rellena cuando el campo no existe.
  const conFlagLineaMedida = (items) => (items || []).map((item) => ({
    ...item,
    allow_custom_line:
      item.allow_custom_line
      ?? products.find((p) => p.id === item.product_id)?.allow_custom_line
      ?? false,
  }));

  // Función para añadir producto a una sección específica
  const addProductToSection = (productId, section) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    // Un producto que SOLO se alquila (precio 0 y plazos pactados) entra ya
    // alquilado, con el plazo mas corto. Si entrara como compra, entraria a
    // 0 EUR y habria que acordarse de cambiarle el pago a mano.
    const soloSeAlquila = tienePlazos(product) && !(Number(product.price) > 0);
    const lineaAlquilada = soloSeAlquila
      ? lineaDeRenting(product, [...product.plazos].sort((a, b) => a.meses - b.meses)[0].meses)
      : null;

    const newItem = {
      product_id: product.id,
      product_ref: product.ref || '',
      product_name: product.name,
      product_type: product.type,
      product_description: product.description || '',
      // Linea a medida: descripcion y precio se pactan aqui, no vienen fijos
      // del catalogo. Lo decide el producto, no un id escrito en el codigo.
      allow_custom_line: !!product.allow_custom_line,
      quantity: 1,
      unit_price: product.price,
      discount: 0,
      subtotal: product.price,
      section: section === 'license' ? 'licenses' : section === 'service' ? 'services' : 'equipment'
    };

    // Include image for equipment products
    if (product.type === 'equipment' && product.image) {
      newItem.product_image = product.image;
    }

    // Si sale alquilada, manda esa: ya trae cuota, plazo y subtotal 0.
    if (lineaAlquilada) {
      Object.assign(newItem, lineaAlquilada);
    }

    // Add recurrence_frequency for license products (default: mensual cada 1)
    if (section === 'license') {
      newItem.recurrence_frequency = 'mensual';
      newItem.recurrence_interval = 1;
      setLicenseItems([...licenseItems, newItem]);
    } else if (section === 'service') {
      setServiceItems([...serviceItems, newItem]);
    } else if (section === 'equipment') {
      setEquipmentItems([...equipmentItems, newItem]);
    }
  };

  // Función para actualizar item
  const updateItem = (index, field, value, section) => {
    const updateItemInSection = (items, setItems) => {
      const updated = [...items];

      // Cambiar la forma de pago rehace la linea entera: la cuota del
      // renting y el precio de venta no se mezclan, y volver a compra
      // tiene que recuperar el precio del catalogo. Ver `pasarARenting`.
      if (field === 'renting_meses') {
        updated[index] = pasarARenting(
          updated[index], productoDe(updated[index], products), value || null);
        setItems(updated);
        return;
      }

      const esNumerico = field === 'quantity' || field === 'discount' || field === 'unit_price';
      // Copia en vez de mutar el objeto que esta en el estado: con la
      // mutacion, un render que llegara antes del setItems ya veia el
      // valor nuevo y el subtotal viejo.
      updated[index] = {
        ...updated[index],
        [field]: esNumerico ? (Number(value) || 0) : value,
      };
      const item = updated[index];

      // Una linea alquilada vale 0 para nosotros SIEMPRE: la paga una
      // financiera. Sin este corte, tocar las unidades la recalcularia
      // como una venta y le colaria la cuota al total del presupuesto.
      if (esLineaDeRenting(item)) {
        item.subtotal = 0;
      } else {
        const discountAmount = (item.unit_price * item.quantity) * (item.discount / 100);
        item.subtotal = (item.unit_price * item.quantity) - discountAmount;
      }

      setItems(updated);
    };

    if (section === 'license') updateItemInSection(licenseItems, setLicenseItems);
    else if (section === 'service') updateItemInSection(serviceItems, setServiceItems);
    else if (section === 'equipment') updateItemInSection(equipmentItems, setEquipmentItems);
  };

  // Función para eliminar item
  const removeItem = (index, section) => {
    if (section === 'license') {
      setLicenseItems(licenseItems.filter((_, i) => i !== index));
    } else if (section === 'service') {
      setServiceItems(serviceItems.filter((_, i) => i !== index));
    } else if (section === 'equipment') {
      setEquipmentItems(equipmentItems.filter((_, i) => i !== index));
    }
  };

  // Cálculo de totales
  const calculateTotals = () => {
    const licenseSubtotal = licenseItems.reduce((sum, item) => sum + item.subtotal, 0);
    const serviceSubtotal = serviceItems.reduce((sum, item) => sum + item.subtotal, 0);
    const equipmentSubtotal = equipmentItems.reduce((sum, item) => sum + item.subtotal, 0);

    const licenseTax = licenseSubtotal * (taxRate / 100);
    const serviceTax = serviceSubtotal * (taxRate / 100);
    const equipmentTax = equipmentSubtotal * (taxRate / 100);

    const totalSubtotal = licenseSubtotal + serviceSubtotal + equipmentSubtotal;
    const totalTax = licenseTax + serviceTax + equipmentTax;
    const total = totalSubtotal + totalTax;

    return {
      licenseSubtotal,
      serviceSubtotal,
      equipmentSubtotal,
      licenseTax,
      serviceTax,
      equipmentTax,
      totalSubtotal,
      totalTax,
      total
    };
  };

  const handleCreateQuote = async () => {
    if (!selectedCustomer) {
      toast.error('Selecciona un cliente');
      return;
    }

    const allItems = [...licenseItems, ...serviceItems, ...equipmentItems];
    if (allItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    try {
      const payload = {
        customer_id: selectedCustomer,
        items: allItems,
        tax_rate: taxRate,
        tax_type: taxType,
        payment_method: paymentMethod,
        notes: quoteNotes || null
      };

      if (editingQuote) {
        // Modo edición — optimistic update: el PUT devuelve el doc actualizado,
        // lo metemos en el state directamente para que la lista se refresque YA.
        const { data: updated } = await axiosInstance.put(`/quotes/${editingQuote.id}`, payload);
        setQuotes((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
        toast.success('Presupuesto actualizado correctamente');
      } else {
        // Modo creación — optimistic update: prepend al state desde la respuesta
        // del POST para evitar el delay del GET /quotes posterior.
        const { data: created } = await axiosInstance.post('/quotes', payload);
        setQuotes((prev) => [created, ...prev]);
        toast.success('Presupuesto creado correctamente');

        // Si viene de una acción "Crear Presupuesto", registrar la acción automáticamente
        const actionTypeParam = searchParams.get('actionType');
        const titleParam = searchParams.get('title');
        const descriptionParam = searchParams.get('description');

        if (actionTypeParam) {
          try {
            await axiosInstance.post('/actions', {
              client_id: selectedCustomer,
              action_type_id: actionTypeParam,
              title: decodeURIComponent(titleParam || 'Presupuesto creado'),
              description: decodeURIComponent(descriptionParam || 'Presupuesto creado desde acción comercial'),
              scheduled_date: new Date().toISOString(),
              assigned_user_id: user?.id,
              status: 'completed'
            });
            toast.success('Acción registrada automáticamente');
          } catch (error) {
            console.error('Error al registrar acción:', error);
          }
        }
      }

      setDialogOpen(false);
      resetForm();
      // Re-fetch como red de seguridad (por si hay totales calculados en backend
      // que no viniesen ya completos en la respuesta del POST/PUT).
      fetchQuotes();
      
      // Si viene de una acción, volver al cliente
      const customerParam = searchParams.get('customer');
      const actionTypeParam = searchParams.get('actionType');
      if (customerParam && actionTypeParam) {
        navigate(`/clients/${customerParam}`);
      }
    } catch (error) {
      toast.error(`Error al ${editingQuote ? 'actualizar' : 'crear'} presupuesto`);
    }
  };

  const resetForm = () => {
    setEditingQuote(null);
    setSelectedCustomer('');
    setLicenseItems([]);
    setServiceItems([]);
    setEquipmentItems([]);
    setTaxRate(21);
    setTaxType('IVA');
    setPaymentMethod('Transferencia');
    setQuoteNotes('');
  };

  const handleEditQuote = (quote) => {
    // Si estamos editando el presupuesto copiado, quitar el parpadeo
    if (copiedQuoteId === quote.id) {
      setCopiedQuoteId(null);
    }
    
    setEditingQuote(quote);
    setSelectedCustomer(quote.customer_id);
    setTaxRate(quote.tax_rate || 21);
    setTaxType(quote.tax_type || 'IVA');
    setPaymentMethod(quote.payment_method || 'Transferencia');
    setQuoteNotes(quote.notes || '');
    
    // Separar items por tipo
    // Por tipo EFECTIVO: los presupuestos del 24-sep tienen lineas con
    // product_type 'renting', y sin traducirlo se perderian al editarlos.
    const licenses = quote.items.filter(item => tipoDeLinea(item) === 'license');
    const services = quote.items.filter(item => tipoDeLinea(item) === 'service');
    const equipment = quote.items.filter(item => tipoDeLinea(item) === 'equipment');
    
    setLicenseItems(conFlagLineaMedida(licenses));
    setServiceItems(conFlagLineaMedida(services));
    setEquipmentItems(conFlagLineaMedida(equipment));

    setDialogOpen(true);
  };

  const handleDownloadPDF = async (id, quoteNumber, customerName) => {
    try {
      const response = await axiosInstance.get(`/quotes/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link with customer name
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Sanitize customer name for filename (remove special characters)
      const safeName = customerName ? customerName.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '_') : '';
      const filename = safeName ? `presupuesto_${quoteNumber}_${safeName}.pdf` : `presupuesto_${quoteNumber}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Presupuesto descargado');
    } catch (error) {
      toast.error('Error al descargar presupuesto');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    // Check if customer has billing alert
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      const customer = customers.find(c => c.id === quote.customer_id);
      if (customer && customer.show_billing_alert && customer.billing_note) {
        setBillingAlertNote(customer.billing_note);
        setPendingQuoteId(quoteId);
        setBillingAlertOpen(true);
        return;
      }
    }
    
    // If no alert, proceed directly
    await proceedWithConversion(quoteId);
  };
  
  const proceedWithConversion = async (quoteId) => {
    try {
      await axiosInstance.post(`/quotes/${quoteId}/convert-to-invoice`);
      toast.success('Facturas generadas correctamente');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar facturas');
    }
  };


  const handleCreateProforma = async (quoteId, customerName) => {
    if (!(await confirmAsync('¿Crear factura PROFORMA? (Solo informativa, no válida fiscalmente)'))) {
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/quotes/${quoteId}/create-proforma`, null, {
        responseType: 'blob'
      });
      
      // Create download link for PDF with customer name
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = customerName ? customerName.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '_') : '';
      const filename = safeName ? `Proforma_${quoteId.slice(0, 8)}_${safeName}.pdf` : `Proforma_${quoteId.slice(0, 8)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Factura proforma generada y descargada');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proforma');
    }
  };


  const handleDeleteQuote = async (quoteId) => {
    if (!(await confirmAsync('¿Estás seguro de que deseas eliminar este presupuesto?'))) {
      return;
    }

    try {
      await axiosInstance.delete(`/quotes/${quoteId}`);
      toast.success('Presupuesto eliminado correctamente');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al eliminar presupuesto');
    }
  };

  const handleCopyQuote = async (quoteId) => {
    try {
      const response = await axiosInstance.post(`/quotes/${quoteId}/copy`);
      toast.success('Presupuesto copiado correctamente');
      
      // Marcar el presupuesto copiado para que parpadee
      setCopiedQuoteId(response.data.id);
      
      // Scroll al presupuesto copiado después de recargar
      await fetchQuotes();
      
      // Quitar el parpadeo cuando se abra/edite el presupuesto
      setTimeout(() => {
        const element = document.querySelector(`[data-quote-id="${response.data.id}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      toast.error('Error al copiar presupuesto');
    }
  };

  /**
   * Email del cliente de un presupuesto.
   *
   * Se pide el cliente por su id en vez de buscarlo en la lista `customers`
   * que ya está en memoria, y ese es el arreglo:
   *
   *   GET /customers está PAGINADO y devuelve 50 de los 2.033 que hay.
   *   `customers.find(...)` solo encontraba a los de la primera página, así
   *   que para casi todos el campo salía vacío. No fallaba siempre, y por eso
   *   parecía aleatorio.
   *
   * Traer los 2.033 para usar uno tampoco es la solución. Se pide ese.
   *
   * Orden de preferencia: el email comercial primero. Un presupuesto lo lee
   * quien decide, no quien paga las facturas; para la factura el orden es el
   * contrario y lo resuelve el backend por su lado.
   */
  const obtenerEmailDelCliente = async (quote) => {
    // Si el presupuesto ya trae el email, no hace falta preguntar nada.
    if (quote.customer_email) return quote.customer_email;

    const enMemoria = customers.find(c => c.id === quote.customer_id);
    if (enMemoria?.email) return enMemoria.email;

    if (!quote.customer_id) return '';
    try {
      const { data } = await axiosInstance.get(`/customers/${quote.customer_id}`);
      return data.email || data.billing_email || data.platform_email || '';
    } catch (e) {
      // Que no se pueda leer el cliente no debe impedir mandar el correo: se
      // abre el diálogo con el campo vacío para escribirlo a mano.
      console.error('No se pudo leer el email del cliente:', e);
      return '';
    }
  };

  const handleOpenEmailDialog = async (quote) => {
    setEmailQuote(quote);

    const customerEmail = await obtenerEmailDelCliente(quote);
    if (!customerEmail) {
      toast.warning('Este cliente no tiene email guardado. Escríbelo a mano.');
    }

    // Obtener configuración de empresa para el nombre comercial
    try {
      const configResponse = await axiosInstance.get('/company-config');
      const tradeName = configResponse.data.trade_name || configResponse.data.name || 'Empresa';

      setEmailRecipients(customerEmail);
      setEmailSubject(`Presupuesto ${quote.quote_number} - ${tradeName}`);
      setEmailMessage(`Estimado/a cliente,\n\nAdjuntamos el presupuesto ${quote.quote_number} para su revisión.\n\nQuedamos a su disposición para cualquier duda o aclaración.\n\nSaludos cordiales,\n${tradeName}`);
      setEmailAttachments([]);
      setEmailDialogOpen(true);
    } catch (error) {
      console.error('Error loading company config:', error);
      setEmailRecipients(customerEmail);
      setEmailSubject(`Presupuesto ${quote.quote_number}`);
      setEmailMessage('');
      setEmailAttachments([]);
      setEmailDialogOpen(true);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Validar cantidad
    if (emailAttachments.length + files.length > 3) {
      toast.error('Máximo 3 archivos adjuntos adicionales');
      return;
    }
    
    // Validar tamaño total (4MB incluyendo el PDF del presupuesto que se agregará en backend)
    const maxSize = 3 * 1024 * 1024; // 3MB para dar espacio al PDF
    let totalSize = 0;
    
    for (const file of files) {
      totalSize += file.size;
    }
    
    if (totalSize > maxSize) {
      toast.error('El tamaño total de archivos excede 3MB');
      return;
    }
    
    // Convertir archivos a base64
    const newAttachments = [];
    for (const file of files) {
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onload = (e) => {
          newAttachments.push({
            filename: file.name,
            content: e.target.result.split(',')[1], // Obtener solo la parte base64
            size: file.size
          });
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    
    setEmailAttachments([...emailAttachments, ...newAttachments]);
  };

  const handleRemoveAttachment = (index) => {
    setEmailAttachments(emailAttachments.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (!emailRecipients.trim()) {
      toast.error('Ingrese al menos un email destinatario');
      return;
    }
    
    if (!emailSubject.trim()) {
      toast.error('Ingrese el asunto del email');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      // Separar emails por comas y limpiar espacios
      const emails = emailRecipients.split(',').map(e => e.trim()).filter(e => e);
      
      const payload = {
        quote_id: emailQuote.id,
        to_emails: emails,
        subject: emailSubject,
        message: emailMessage,
        additional_attachments: emailAttachments
      };
      
      const response = await axiosInstance.post(`/quotes/${emailQuote.id}/send-email`, payload);
      
      // Entrega parcial: sale en rojo y sin cerrarse solo. Un aviso verde que
      // dice "no se pudo enviar a X" se lee como bueno y no se lee entero.
      const noEntregados = response.data.failed || [];
      if (noEntregados.length > 0) {
        toast.error(response.data.message, { duration: 10000 });
      } else {
        toast.success(response.data.message || 'Email enviado correctamente');
      }
      setEmailDialogOpen(false);

      // El link de pago se genera al enviar, dentro del propio email. Sin este
      // refresco la tarjeta no lo enseñaria hasta recargar la pantalla.
      fetchQuotes();
      fetchPaymentLinks();

      // Limpiar el formulario
      setEmailQuote(null);
      setEmailRecipients('');
      setEmailSubject('');
      setEmailMessage('');
      setEmailAttachments([]);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = (quote) => {
    // Verificar que el usuario tenga teléfono configurado
    if (!user?.phone) {
      toast.error('Por favor, configura tu número de teléfono en tu perfil de usuario');
      return;
    }

    // Obtener el cliente
    const customer = customers.find(c => c.id === quote.customer_id);
    if (!customer) {
      toast.error('No se encontró el cliente');
      return;
    }

    // Calcular importe de transferencia (servicios + equipos)
    const transferAmount = (quote.services_total || 0) + (quote.equipment_total || 0);

    // Si hay link de pago vivo se ofrece como alternativa, no como sustituto:
    // el cliente elige por donde paga. En cuanto paga por uno, el backend
    // cierra el otro (ver cobros.py), asi que no puede pagar dos veces.
    const pago = paymentLinks[quote.id];
    const bloqueLink = pago?.activo
      ? `\n💳 *O PAGUE CON TARJETA*\n${pago.url}\nPagando por aquí no hace falta justificante.\n`
      : '';

    // Crear mensaje
    const message = `Hola, una vez aceptado el presupuesto ${quote.quote_number}, le envío los datos para realizar la transferencia:

📋 *DATOS TRANSFERENCIA*

*Pago inicial*
*Cuenta Bancaria:* ${companyConfig?.bank_account || 'No configurado'}
*Concepto:* Pago ${quote.quote_number}
*Titular de la cuenta:* ${companyConfig?.name || 'Empresa'}
*Importe de Transferencia:* ${transferAmount.toFixed(2)}€
${bloqueLink}
Por favor, envíeme el justificante de pago para su revisión y acelerar el proceso.

Gracias.`;

    // Limpiar el número de teléfono del usuario (quitar espacios, guiones, etc.)
    const phoneNumber = user.phone.replace(/\s/g, '').replace(/-/g, '');

    // Crear URL de WhatsApp
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await axiosInstance.patch(`/quotes/${quoteId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success('Estado actualizado correctamente');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // ============= PLANTILLAS =============
  const handleCreateTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Ingresa un nombre para la plantilla');
      return;
    }

    const allItems = [...licenseItems, ...serviceItems, ...equipmentItems];
    if (allItems.length === 0) {
      toast.error('Agrega al menos un producto a la plantilla');
      return;
    }

    try {
      const payload = {
        name: templateName,
        description: templateDescription,
        items: allItems,
        tax_rate: taxRate,
        tax_type: taxType,
        payment_method: paymentMethod
      };

      if (editingTemplate) {
        await axiosInstance.put(`/quote-templates/${editingTemplate.id}`, payload);
        toast.success('Plantilla actualizada');
      } else {
        await axiosInstance.post('/quote-templates', payload);
        toast.success('Plantilla creada');
      }

      setTemplateDialogOpen(false);
      resetTemplateForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Error al guardar plantilla');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setLicenseItems(conFlagLineaMedida(template.items.filter(i => i.section === 'licenses')));
    setServiceItems(conFlagLineaMedida(template.items.filter(i => i.section === 'services')));
    // 'renting' era una seccion el 24-sep; las plantillas de aquel dia la
    // traen y sus lineas son equipos.
    setEquipmentItems(conFlagLineaMedida(template.items.filter(
      i => i.section === 'equipment' || i.section === 'renting')));
    setTaxRate(template.tax_rate);
    setTaxType(template.tax_type);
    setPaymentMethod(template.payment_method);
    setTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!(await confirmAsync('¿Eliminar esta plantilla?'))) return;

    try {
      await axiosInstance.delete(`/quote-templates/${templateId}`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const handleLoadTemplate = (template) => {
    setLicenseItems(conFlagLineaMedida(template.items.filter(i => i.section === 'licenses')));
    setServiceItems(conFlagLineaMedida(template.items.filter(i => i.section === 'services')));
    // 'renting' era una seccion el 24-sep; las plantillas de aquel dia la
    // traen y sus lineas son equipos.
    setEquipmentItems(conFlagLineaMedida(template.items.filter(
      i => i.section === 'equipment' || i.section === 'renting')));
    setTaxRate(template.tax_rate);
    setTaxType(template.tax_type);
    setPaymentMethod(template.payment_method);
    toast.success(`Plantilla "${template.name}" cargada`);
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setLicenseItems([]);
    setServiceItems([]);
    setEquipmentItems([]);
    setTaxRate(21);
    setTaxType('IVA');
    setPaymentMethod('Transferencia');
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="bg-gray-100 text-gray-800">Borrador</Badge>,
      sent: <Badge className="bg-blue-100 text-blue-800">Enviado</Badge>,
      accepted: <Badge className="bg-green-100 text-green-800">Aceptado</Badge>,
      rejected: <Badge className="bg-red-100 text-red-800">Rechazado</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  // Filtrar presupuestos
  const filteredQuotes = quotes.filter(quote => {
    // Filtro por término de búsqueda (nombre cliente o número de presupuesto)
    // Defensive: customer_name / quote_number pueden ser null en datos antiguos.
    const term = searchTerm.toLowerCase();
    const customerName = (quote.customer_name || '').toLowerCase();
    const quoteNumber = (quote.quote_number || '').toLowerCase();
    const matchesSearch = searchTerm === '' ||
      customerName.includes(term) ||
      quoteNumber.includes(term);
    
    // Filtro por estado de facturación
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'invoiced' && quote.invoice_status === 'invoiced') ||
      (statusFilter === 'pending' && quote.invoice_status === 'pending');
    
    // Filtro por estado
    const matchesQuoteStatus = filterStatus === 'all' || quote.status === filterStatus;
    
    // Filtro por cliente
    const matchesCustomer = filterCustomer === 'all' || quote.customer_id === filterCustomer;
    
    return matchesSearch && matchesStatus && matchesQuoteStatus && matchesCustomer;
  });


  const totals = calculateTotals();

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="quotes-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#212B35] mb-2" data-testid="quotes-title">Presupuestos y Plantillas</h1>
            <p className="text-[#212B35]/70">Gestiona tus presupuestos y plantillas</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm(); // Reset form when dialog closes
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white" data-testid="create-quote-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Presupuesto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingQuote ? `Editar Presupuesto ${editingQuote.quote_number}` : 'Crear Nuevo Presupuesto'}
                </DialogTitle>
                <DialogDescription>Organiza productos por secciones: Licencias, Servicios y Equipos</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Cliente y configuración */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <ClientSearchSelect
                    label="Cliente"
                    value={selectedCustomer}
                    onValueChange={setSelectedCustomer}
                    required={true}
                  />
                  <div>
                    <Label>Impuesto</Label>
                    <Select value={taxType} onValueChange={setTaxType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IVA">IVA (21%)</SelectItem>
                        <SelectItem value="IGIC">IGIC (0%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>% Impuesto</Label>
                    <Input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <Label>Forma de Pago</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Transferencia">Transferencia</SelectItem>
                        <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Financiacion">Financiación</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Notas del presupuesto */}
                <div>
                  <Label>Notas del Presupuesto (aparecerán en el PDF)</Label>
                  <textarea
                    value={quoteNotes}
                    onChange={(e) => setQuoteNotes(e.target.value)}
                    placeholder="Añade notas o condiciones especiales para este presupuesto..."
                    className="w-full mt-1 p-3 border border-gray-300 rounded-lg text-sm min-h-[80px] resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Cargar desde plantilla */}
                {!editingQuote && templates.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label className="mb-2 block">Cargar desde plantilla (opcional)</Label>
                    <div className="flex gap-2 flex-wrap">
                      {templates.map(template => (
                        <Button
                          key={template.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleLoadTemplate(template)}
                          className="text-sm"
                        >
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Secciones de productos */}
                <Tabs defaultValue="licenses" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="licenses">
                      Licencias ({licenseItems.length})
                    </TabsTrigger>
                    <TabsTrigger value="services">
                      Servicios ({serviceItems.length})
                    </TabsTrigger>
                    <TabsTrigger value="equipment">
                      Equipos ({equipmentItems.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="licenses" className="mt-4">
                    <ItemTable items={licenseItems} section="license" sectionName="LICENCIAS (Pago Recurrente)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                  </TabsContent>
                  
                  <TabsContent value="services" className="mt-4">
                    <ItemTable items={serviceItems} section="service" sectionName="SERVICIOS (Pago Único)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                  </TabsContent>
                  
                  <TabsContent value="equipment" className="mt-4">
                    <ItemTable items={equipmentItems} section="equipment" sectionName="EQUIPOS (Pago Único)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                  </TabsContent>
                </Tabs>

                {/* Resumen de totales */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <Card className="p-4">
                      <div className="text-sm text-[#212B35]/70">Licencias</div>
                      <div className="text-lg font-bold text-[#9C0F17]">{totals.licenseSubtotal.toFixed(2)}€</div>
                      <div className="text-xs text-[#212B35]/50">+ {totals.licenseTax.toFixed(2)}€ {taxType}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-[#212B35]/70">Servicios</div>
                      <div className="text-lg font-bold text-green-700">{totals.serviceSubtotal.toFixed(2)}€</div>
                      <div className="text-xs text-[#212B35]/50">+ {totals.serviceTax.toFixed(2)}€ {taxType}</div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-sm text-[#212B35]/70">Equipos</div>
                      <div className="text-lg font-bold text-purple-700">{totals.equipmentSubtotal.toFixed(2)}€</div>
                      <div className="text-xs text-[#212B35]/50">+ {totals.equipmentTax.toFixed(2)}€ {taxType}</div>
                    </Card>
                    <Card className="p-4 border-[#9C0F17]">
                      <div className="text-sm text-[#212B35]/70">TOTAL</div>
                      <div className="text-2xl font-bold text-[#9C0F17]">{totals.total.toFixed(2)}€</div>
                      <div className="text-xs text-[#212B35]/50">{paymentMethod}</div>
                    </Card>
                  </div>
                  
                  <Button onClick={handleCreateQuote} className="w-full bg-[#9C0F17] hover:bg-[#7A0C12]">
                    <Calculator className="w-4 h-4 mr-2" />
                    {editingQuote ? 'Actualizar Presupuesto' : 'Crear Presupuesto'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs principales */}
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList>
            <TabsTrigger value="quotes">Presupuestos</TabsTrigger>
            <TabsTrigger value="templates">Plantillas</TabsTrigger>
          </TabsList>

          {/* TAB: PRESUPUESTOS */}
          <TabsContent value="quotes" className="space-y-6">
            {/* Filtros de búsqueda */}
            <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="search-quotes">Buscar por cliente o número</Label>
                <Input
                  id="search-quotes"
                  placeholder="Ej: Empresa o PRE-2025-0001"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="filter-status">Filtrar por estado</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger id="filter-status" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="sent">Enviado</SelectItem>
                    <SelectItem value="accepted">Aceptado</SelectItem>
                    <SelectItem value="rejected">Rechazado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-invoice-status">Estado de facturación</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="filter-invoice-status" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="invoiced">Facturados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setStatusFilter('all');
                  }}
                  className="w-full"
                >
                  Limpiar filtros
                </Button>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              Mostrando {filteredQuotes.length} de {quotes.length} presupuestos
            </div>
          </CardContent>
        </Card>

        {/* Lista de presupuestos */}
        <div className="grid grid-cols-1 gap-4">
          {filteredQuotes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">
                  {quotes.length === 0 ? 'No hay presupuestos aún.' : 'No se encontraron presupuestos con los filtros aplicados.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredQuotes.map((quote) => (
              <Card 
                key={quote.id} 
                className={`shadow-lg transition-all ${copiedQuoteId === quote.id ? 'animate-pulse ring-4 ring-green-400' : ''}`}
                data-testid={`quote-card-${quote.id}`}
                data-quote-id={quote.id}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle data-testid="quote-number">{quote.quote_number}</CardTitle>
                      <p className="text-sm font-semibold text-slate-800 mt-1">
                        Cliente: {quote.customer_name || 'Sin cliente'}
                      </p>
                      <p className="text-sm text-slate-600">
                        {new Date(quote.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={quote.status} onValueChange={(value) => handleStatusChange(quote.id, value)}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                              Borrador
                            </div>
                          </SelectItem>
                          <SelectItem value="sent">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              Enviado
                            </div>
                          </SelectItem>
                          <SelectItem value="accepted">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              Aceptado
                            </div>
                          </SelectItem>
                          <SelectItem value="rejected">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              Rechazado
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={() => handleOpenEmailDialog(quote)}
                      className="bg-[#0066B3] hover:bg-[#004A80] text-white"
                      data-testid="send-quote-email-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Enviar por Email
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDownloadPDF(quote.id, quote.quote_number, quote.customer_name)}
                      className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                      data-testid="download-quote-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleEditQuote(quote)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="edit-quote-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleCopyQuote(quote.id)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="copy-quote-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar
                    </Button>
                    {quote.status === 'accepted' && (
                      <>
                        {quote.invoice_status !== 'invoiced' && (
                          <>
                            {/* Que le falta al cliente para poder facturarle.
                                Lee el mismo valor que pinta el CRM, asi que
                                las dos pantallas no pueden discrepar. Se abre
                                el mismo formulario desde los dos sitios. */}
                            {!quote.billing_ready && (
                              <div className="w-full mb-2 border border-amber-200 bg-amber-50 rounded px-3 py-2"
                                   data-testid={`faltan-datos-${quote.id}`}>
                                <AvisoFaltanDatos
                                  estado={quote}
                                  onAbrir={() => setQuoteDatos(quote)}
                                />
                                <p className="text-xs text-amber-800 mt-0.5">
                                  {(quote.billing_missing || []).join(' · ')}
                                </p>
                              </div>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleCreateProforma(quote.id, quote.customer_name)}
                              className="bg-orange-600 hover:bg-orange-700"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Crear Proforma
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSendWhatsApp(quote)}
                              className="bg-green-500 hover:bg-green-600"
                              title="Enviar datos de transferencia por WhatsApp"
                            >
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                              </svg>
                              WhatsApp
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleConvertToInvoice(quote.id)}
                              disabled={!quote.billing_ready}
                              title={quote.billing_ready
                                ? 'Generar las facturas de este presupuesto'
                                : `Faltan datos: ${(quote.billing_missing || []).join(', ')}`}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-slate-300"
                              data-testid="convert-to-invoice-btn"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Convertir a Factura
                            </Button>
                          </>
                        )}
                        {quote.invoice_status === 'invoiced' && (
                          <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-md text-sm font-medium">
                            ✓ Ya facturado
                          </div>
                        )}
                      </>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleDeleteQuote(quote.id)}
                      variant="destructive"
                      data-testid="delete-quote-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Borrar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-3 bg-[#9C0F17]/5 rounded-lg border border-[#9C0F17]/20">
                      <p className="text-sm text-[#212B35]/70">Licencias (Mensual)</p>
                      <p className="text-lg font-bold text-[#9C0F17]" data-testid="licenses-total">
                        {quote.licenses_total?.toFixed(2) || '0.00'}€
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-[#212B35]/70">Servicios</p>
                      <p className="text-lg font-bold text-green-700" data-testid="services-total">
                        {quote.services_total?.toFixed(2) || '0.00'}€
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-[#212B35]/70">Equipos</p>
                      <p className="text-lg font-bold text-purple-700" data-testid="equipment-total">
                        {quote.equipment_total?.toFixed(2) || '0.00'}€
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-slate-600">Total ({quote.tax_type || 'IVA'} incl.)</span>
                    <span className="text-2xl font-bold text-slate-900" data-testid="quote-total">
                      {quote.total?.toFixed(2) || '0.00'}€
                    </span>
                  </div>

                  {/* El renting, DEBAJO del total y fuera de el: lo
                      factura la entidad que lo financia. Meterlo en el
                      numero grande diria que cobramos algo que no
                      cobramos. Se calcula de las lineas y no se guarda
                      aparte: un total denormalizado que nadie recalcula
                      acaba discrepando de sus propias lineas. */}
                  {(() => {
                    const renting = resumenRenting(quote.items);
                    if (!renting.hay) return null;
                    return (
                      <div className="flex justify-between items-center pt-2 text-sm">
                        <span className="text-amber-800">
                          Renting (lo financia una entidad ajena)
                        </span>
                        <span className="font-bold text-amber-900" data-testid="renting-total">
                          {renting.cuotaMes.toFixed(2)}€/mes
                          {renting.meses.length === 1 && ` · ${renting.meses[0]} meses`}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Cobro por link de la parte no recurrente.
                      Convive con los datos de transferencia a proposito: el
                      cliente elige por donde paga. Lo que no puede quedar vivo
                      es el otro camino una vez ha pagado por uno, y de eso se
                      encarga el backend (ver cobros.py). */}
                  {paymentLinks[quote.id]?.existe && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      paymentLinks[quote.id].estado === 'pagado'
                        ? 'bg-emerald-50 border-emerald-200'
                        : paymentLinks[quote.id].activo
                          ? 'bg-slate-50 border-slate-200'
                          : 'bg-amber-50 border-amber-200'
                    }`}>
                      {paymentLinks[quote.id].estado === 'pagado' ? (
                        <>
                          <h4 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> COBRADO
                          </h4>
                          <div className="text-sm space-y-1">
                            <div>
                              <span className="font-medium text-slate-700">Importe:</span>
                              <span className="ml-2 font-bold text-emerald-900">
                                {paymentLinks[quote.id].importe?.toFixed(2)}€
                              </span>
                            </div>
                            {paymentLinks[quote.id].pagado_en && (
                              <div>
                                <span className="font-medium text-slate-700">Fecha:</span>
                                <span className="ml-2 text-slate-900">
                                  {new Date(paymentLinks[quote.id].pagado_en).toLocaleDateString('es-ES')}
                                  {paymentLinks[quote.id].metodo_pago && ` · ${paymentLinks[quote.id].metodo_pago}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </>
                      ) : paymentLinks[quote.id].activo ? (
                        <>
                          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <CreditCard className="w-4 h-4" /> LINK DE PAGO
                          </h4>
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="font-medium text-slate-700">Importe:</span>
                              <span className="ml-2 font-bold text-slate-900">
                                {paymentLinks[quote.id].importe?.toFixed(2)}€
                              </span>
                            </div>
                            {paymentLinks[quote.id].caduca_en && (
                              <div className="text-slate-600">
                                Válido hasta el {new Date(paymentLinks[quote.id].caduca_en).toLocaleDateString('es-ES')}
                              </div>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copiarLink(paymentLinks[quote.id].url)}
                              title="Copiar el link para enviarlo al cliente"
                            >
                              <Copy className="w-3 h-3 mr-1" /> Copiar link
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {paymentLinks[quote.id].motivo_anulacion === 'cobrado_por_otra_via'
                              ? 'COBRADO POR OTRA VÍA'
                              : paymentLinks[quote.id].estado === 'caducado'
                                ? 'LINK CADUCADO'
                                : 'LINK ANULADO'}
                          </h4>
                          <p className="text-sm text-slate-700 mb-3">
                            {paymentLinks[quote.id].motivo_anulacion === 'cobrado_por_otra_via'
                              ? 'El cliente pagó por transferencia. El link se cerró para que no pueda pagar dos veces.'
                              : paymentLinks[quote.id].motivo_anulacion === 'importe_modificado'
                                ? 'Se anuló porque cambió el importe del presupuesto.'
                                : 'El cliente no llegó a pagarlo a tiempo.'}
                          </p>
                          {/* Sin boton si ya esta cobrado: seria ofrecer un link
                              vivo a quien ya ha pagado. El backend lo rechaza
                              igualmente con un 409, pero un boton que solo sirve
                              para dar un error no deberia llegar a existir. */}
                          {paymentLinks[quote.id].motivo_anulacion !== 'cobrado_por_otra_via' && (
                            <Button
                              size="sm"
                              onClick={() => regenerarLink(quote.id)}
                              disabled={regenerandoLink === quote.id}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {regenerandoLink === quote.id ? 'Generando...' : 'Generar link nuevo'}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Datos de transferencia - Solo si está aceptado */}
                  {quote.status === 'accepted' && companyConfig && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-3">📋 DATOS TRANSFERENCIA</h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-slate-700">Pago inicial</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Cuenta Bancaria:</span>
                          <span className="ml-2 text-slate-900">{companyConfig.bank_account || 'No configurado'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Concepto:</span>
                          <span className="ml-2 text-slate-900">Pago {quote.quote_number}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Titular de la cuenta:</span>
                          <span className="ml-2 text-slate-900">{companyConfig.name || 'Empresa'}</span>
                        </div>
                        <div className="pt-2 border-t border-blue-300">
                          <span className="font-medium text-slate-700">Importe de Transferencia:</span>
                          <span className="ml-2 text-lg font-bold text-blue-900">
                            {((quote.services_total || 0) + (quote.equipment_total || 0)).toFixed(2)}€
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
            </div>
          </TabsContent>

          {/* TAB: PLANTILLAS */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">Crea plantillas reutilizables para presupuestos</p>
              <Dialog open={templateDialogOpen} onOpenChange={(open) => {
                setTemplateDialogOpen(open);
                if (!open) resetTemplateForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
                    </DialogTitle>
                    <DialogDescription>Define productos y configuración para reutilizar</DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre de la plantilla *</Label>
                        <Input
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          placeholder="Ej: Paquete Básico, Servicios Premium..."
                        />
                      </div>
                      <div>
                        <Label>Descripción (opcional)</Label>
                        <Input
                          value={templateDescription}
                          onChange={(e) => setTemplateDescription(e.target.value)}
                          placeholder="Breve descripción de la plantilla"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label>% {taxType}</Label>
                        <Input
                          type="number"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <Label>Tipo de Impuesto</Label>
                        <Select value={taxType} onValueChange={setTaxType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IVA">IVA</SelectItem>
                            <SelectItem value="IGIC">IGIC</SelectItem>
                            <SelectItem value="IPSI">IPSI</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Método de Pago</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Transferencia">Transferencia</SelectItem>
                            <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                            <SelectItem value="Efectivo">Efectivo</SelectItem>
                            <SelectItem value="Financiacion">Financiación</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Tabs defaultValue="licenses" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="licenses">Licencias ({licenseItems.length})</TabsTrigger>
                        <TabsTrigger value="services">Servicios ({serviceItems.length})</TabsTrigger>
                        <TabsTrigger value="equipment">Equipos ({equipmentItems.length})</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="licenses" className="mt-4">
                        <ItemTable items={licenseItems} section="license" sectionName="LICENCIAS (Pago Recurrente)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                      </TabsContent>
                      
                      <TabsContent value="services" className="mt-4">
                        <ItemTable items={serviceItems} section="service" sectionName="SERVICIOS (Pago Único)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                      </TabsContent>
                      
                      <TabsContent value="equipment" className="mt-4">
                        <ItemTable items={equipmentItems} section="equipment" sectionName="EQUIPOS (Pago Único)"
                                       products={products}
                                       onAddProduct={addProductToSection}
                                       updateItem={updateItem}
                                       removeItem={removeItem} />
                      </TabsContent>
                    </Tabs>

                    <Button onClick={handleCreateTemplate} className="w-full bg-[#9C0F17] hover:bg-[#7A0C12]">
                      {editingTemplate ? 'Actualizar Plantilla' : 'Crear Plantilla'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-gray-500">
                    No hay plantillas creadas
                  </CardContent>
                </Card>
              ) : (
                templates.map(template => (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="text-lg">{template.name}</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {template.description && (
                        <p className="text-sm text-gray-600">{template.description}</p>
                      )}
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Productos:</span>
                          <span className="font-semibold">{template.items.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{template.tax_type}:</span>
                          <span className="font-semibold">{template.tax_rate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pago:</span>
                          <span className="font-semibold">{template.payment_method}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Billing Alert Dialog */}
        <Dialog open={billingAlertOpen} onOpenChange={(open) => {
          setBillingAlertOpen(open);
          if (!open && pendingQuoteId) {
            // If user closes without clicking button, cancel conversion
            setPendingQuoteId(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertCircle className="w-5 h-5" />
                Nota de Facturación
              </DialogTitle>
              <DialogDescription>
                Este cliente tiene una nota importante sobre facturación
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {billingAlertNote}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setBillingAlertOpen(false);
                  setPendingQuoteId(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  setBillingAlertOpen(false);
                  if (pendingQuoteId) {
                    await proceedWithConversion(pendingQuoteId);
                    setPendingQuoteId(null);
                  }
                }}
                className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
              >
                Continuar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#0066B3]" />
                Enviar Presupuesto por Email
              </DialogTitle>
              <DialogDescription>
                {emailQuote && `Presupuesto ${emailQuote.quote_number} - Se adjuntará automáticamente el PDF`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Destinatarios */}
              <div className="space-y-2">
                <Label htmlFor="email-recipients">Destinatarios *</Label>
                <Input
                  id="email-recipients"
                  type="text"
                  placeholder="ejemplo@email.com, otro@email.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500">Separar múltiples emails con comas</p>
              </div>

              {/* Asunto */}
              <div className="space-y-2">
                <Label htmlFor="email-subject">Asunto *</Label>
                <Input
                  id="email-subject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  required
                />
              </div>

              {/* Mensaje */}
              <div className="space-y-2">
                <Label htmlFor="email-message">Mensaje</Label>
                <Textarea
                  id="email-message"
                  rows={8}
                  placeholder="Escriba el mensaje del email aquí..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  className="resize-none"
                />
              </div>

              {/* Adjuntos adicionales */}
              <div className="space-y-2">
                <Label>Adjuntos adicionales (máx. 3 archivos, 4MB total)</Label>
                
                {emailAttachments.length < 3 && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      onChange={handleFileUpload}
                      multiple
                      className="hidden"
                      id="file-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Seleccionar archivos
                    </Button>
                  </div>
                )}

                {/* Lista de archivos adjuntos */}
                {emailAttachments.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {emailAttachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">{file.filename}</span>
                          <span className="text-xs text-gray-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAttachment(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info sobre botones aceptar/rechazar */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ℹ️ El email incluirá botones para que el cliente pueda <strong>Aceptar</strong> o <strong>Rechazar</strong> el presupuesto directamente. Serás notificado automáticamente de su decisión.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
                disabled={sendingEmail}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="bg-[#0066B3] hover:bg-[#004A80] text-white"
              >
                {sendingEmail ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Email
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <DialogoDatosFacturacion
          open={!!quoteDatos}
          onOpenChange={(v) => !v && setQuoteDatos(null)}
          customerId={quoteDatos?.customer_id}
          nombre={quoteDatos?.customer_name}
          onGuardado={fetchQuotes}
        />
      </div>
    </Layout>
  );
}
